import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './ColourPicker.css';

/**
 * Returns 'dark' or 'light' based on the perceived luminance of a hex colour.
 * Uses the YIQ formula (weighted for human eye sensitivity).
 */
export const getTextColour = (hex) => {
    if (!hex || hex.length < 7) return 'dark';
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 150 ? 'dark' : 'light';
};

// --- Colour conversions (ported from marketing-v2 ColorPicker) ---
const hsbToHex = (h, s, b) => {
    s = s / 100; b = b / 100;
    const k = (n) => (n + h / 60) % 6;
    const f = (n) => b * (1 - s * Math.max(0, Math.min(k(n), 4 - k(n), 1)));
    const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
    return `#${toHex(f(5))}${toHex(f(3))}${toHex(f(1))}`;
};

const hexToHsb = (hex) => {
    if (!hex || hex.length < 7) return { h: 0, s: 0, v: 100 };
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === r) h = ((g - b) / d + 6) % 6;
        else if (max === g) h = (b - r) / d + 2;
        else h = (r - g) / d + 4;
        h *= 60;
    }
    return { h, s: max === 0 ? 0 : (d / max) * 100, v: max * 100 };
};

// --- localStorage for saved colours ---
const SAVED_KEY = 'beu-saved-diary-colors';
const loadSaved = () => { try { return JSON.parse(localStorage.getItem(SAVED_KEY)) || []; } catch { return []; } };
const persistSaved = (c) => { try { localStorage.setItem(SAVED_KEY, JSON.stringify(c)); } catch {} };

// --- Curated palette of 24 individual swatches ---
const SWATCHES = [
    { hex: '#c8eeee', name: 'Seafoam' }, { hex: '#d4e8f7', name: 'Sky' },
    { hex: '#ddd6f3', name: 'Lavender' }, { hex: '#f3d6e8', name: 'Rose' },
    { hex: '#fde2c8', name: 'Peach' }, { hex: '#fef3c7', name: 'Honey' },
    { hex: '#e8e4df', name: 'Sand' }, { hex: '#d5cfc6', name: 'Oat' },
    { hex: '#c7d2cb', name: 'Sage' }, { hex: '#d1dce5', name: 'Mist' },
    { hex: '#e0d5c9', name: 'Latte' }, { hex: '#f0ebe5', name: 'Cream' },
    { hex: '#007EA0', name: 'Teal' }, { hex: '#4a7c6f', name: 'Forest' },
    { hex: '#6366a0', name: 'Iris' }, { hex: '#a0627a', name: 'Berry' },
    { hex: '#c27834', name: 'Amber' }, { hex: '#7a8590', name: 'Slate' },
    { hex: '#29282D', name: 'Charcoal' }, { hex: '#3d4f5f', name: 'Navy' },
    { hex: '#4a3f5c', name: 'Plum' }, { hex: '#5c3d3d', name: 'Burgundy' },
    { hex: '#4a5040', name: 'Olive' }, { hex: '#6b6b6b', name: 'Stone' },
];

// --- 10 curated palettes (8 named colours each, no repeats) ---
// Exported (read only) so other surfaces can theme from the same set, for
// example the staging diary demo. Adding an export changes nothing here.
export const PALETTES = [
    { name: 'Coastal', colours: [
        { hex: '#E8F4F3', name: 'Foam' },       { hex: '#C8EEEE', name: 'Seafoam' },
        { hex: '#93C4D1', name: 'Surf' },        { hex: '#5F97AA', name: 'Ocean' },
        { hex: '#2D5F6E', name: 'Deep' },        { hex: '#18363E', name: 'Abyss' },
        { hex: '#F5E6D3', name: 'Sand' },        { hex: '#C2B9AC', name: 'Driftwood' },
    ]},
    { name: 'Botanical', colours: [
        { hex: '#E6F0E6', name: 'Sprout' },      { hex: '#AAD47B', name: 'Lime' },
        { hex: '#80BA6E', name: 'Fern' },        { hex: '#4A7C6F', name: 'Eucalyptus' },
        { hex: '#3E805B', name: 'Forest' },      { hex: '#30694B', name: 'Pine' },
        { hex: '#EDE5D0', name: 'Pollen' },      { hex: '#A89F91', name: 'Bark' },
    ]},
    { name: 'Sunset', colours: [
        { hex: '#FFF285', name: 'Glow' },        { hex: '#FBCB8E', name: 'Peach' },
        { hex: '#F4957A', name: 'Coral' },       { hex: '#EF6F4D', name: 'Ember' },
        { hex: '#C9553D', name: 'Flame' },       { hex: '#8B3A4A', name: 'Rosewood' },
        { hex: '#4B3756', name: 'Dusk' },        { hex: '#2D2438', name: 'Night' },
    ]},
    { name: 'Nordic', colours: [
        { hex: '#F8F6F7', name: 'Snow' },        { hex: '#EBE5DD', name: 'Linen' },
        { hex: '#D7E1EA', name: 'Frost' },       { hex: '#C4B59F', name: 'Birch' },
        { hex: '#A08F7F', name: 'Clay' },        { hex: '#8B8680', name: 'Flint' },
        { hex: '#505862', name: 'Storm' },       { hex: '#2F3439', name: 'Charcoal' },
    ]},
    { name: 'Warm', colours: [
        { hex: '#FEF3C7', name: 'Honey' },       { hex: '#FDE2C8', name: 'Apricot' },
        { hex: '#F3D6E8', name: 'Blush' },       { hex: '#D2B48C', name: 'Caramel' },
        { hex: '#C27834', name: 'Amber' },       { hex: '#A0627A', name: 'Berry' },
        { hex: '#5C3D3D', name: 'Burgundy' },    { hex: '#3D2B2B', name: 'Cocoa' },
    ]},
    { name: 'Tokyo Neon', colours: [
        { hex: '#F0ABFC', name: 'Sakura' },      { hex: '#E879F9', name: 'Neon Pink' },
        { hex: '#A855F7', name: 'Ultra Violet' },{ hex: '#7C3AED', name: 'Electric Purple' },
        { hex: '#06B6D4', name: 'Cyan' },        { hex: '#2DD4BF', name: 'Mint Glow' },
        { hex: '#1E1B4B', name: 'Midnight' },    { hex: '#0F172A', name: 'Deep Space' },
    ]},
    { name: 'Miami Vice', colours: [
        { hex: '#FBCFE8', name: 'Flamingo' },    { hex: '#F472B6', name: 'Hot Pink' },
        { hex: '#EC4899', name: 'Fuchsia' },     { hex: '#60A5FA', name: 'Sky Blue' },
        { hex: '#38BDF8', name: 'Pool' },        { hex: '#0EA5E9', name: 'Ocean Drive' },
        { hex: '#FDE68A', name: 'Palm Gold' },   { hex: '#FB923C', name: 'Tangerine' },
    ]},
    { name: 'Jewel', colours: [
        { hex: '#E8DDD1', name: 'Pearl' },       { hex: '#F8DBB1', name: 'Topaz' },
        { hex: '#D2BDA2', name: 'Bronze' },      { hex: '#469496', name: 'Aquamarine' },
        { hex: '#0A8C8A', name: 'Teal' },        { hex: '#7B4E9E', name: 'Amethyst' },
        { hex: '#950060', name: 'Ruby' },        { hex: '#24513D', name: 'Emerald' },
    ]},
    { name: 'Tropical', colours: [
        { hex: '#FFBA49', name: 'Sunflower' },   { hex: '#20A39E', name: 'Sea Green' },
        { hex: '#EF5B5B', name: 'Coral Red' },   { hex: '#FF85A1', name: 'Hibiscus' },
        { hex: '#45B7D1', name: 'Lagoon' },      { hex: '#2D6A4F', name: 'Jungle' },
        { hex: '#A4A9AD', name: 'Cool Steel' },  { hex: '#23001E', name: 'Midnight Plum' },
    ]},
    { name: 'Studio', colours: [
        { hex: '#F3F4F6', name: 'Canvas' },      { hex: '#DDD6F3', name: 'Wisteria' },
        { hex: '#D1C4E9', name: 'Lilac' },       { hex: '#A78BFA', name: 'Violet' },
        { hex: '#8B5CF6', name: 'Purple' },      { hex: '#5B21B6', name: 'Indigo' },
        { hex: '#4A3F5C', name: 'Plum' },        { hex: '#1F2041', name: 'Ink' },
    ]},
];

// Find colour name for a hex value (checks palettes first, then swatches)
const findColourName = (hex) => {
    if (!hex) return null;
    const lower = hex.toLowerCase();
    // Check palettes
    for (const p of PALETTES) {
        const match = p.colours.find(c => c.hex.toLowerCase() === lower);
        if (match) return match.name;
    }
    // Check swatches
    const swatch = SWATCHES.find(s => s.hex.toLowerCase() === lower);
    if (swatch) return swatch.name;
    return null;
};

const TABS = [
    { id: 'palettes', label: 'Palettes' },
    { id: 'swatches', label: 'Swatches' },
    { id: 'picker', label: 'Picker' },
];

const ColourPicker = ({ value = '#c8eeee', onChange, onReset, resetLabel = 'Reset', title = 'Diary Colour', inline = false, palettes = true, paletteAa = true }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(palettes ? 'palettes' : 'swatches');
    const [hexInput, setHexInput] = useState(value);
    const [savedColors, setSavedColors] = useState(loadSaved);

    // HSB state for spectrum picker
    const [hue, setHue] = useState(0);
    const [saturation, setSaturation] = useState(0);
    const [brightness, setBrightness] = useState(100);
    const [isDraggingSpectrum, setIsDraggingSpectrum] = useState(false);
    const [isDraggingHue, setIsDraggingHue] = useState(false);

    const spectrumRef = useRef(null);
    const hueRef = useRef(null);

    // Sync hex input and HSB when value changes
    useEffect(() => {
        setHexInput(value);
        if (/^#[0-9a-fA-F]{6}$/.test(value)) {
            const hsb = hexToHsb(value);
            setHue(hsb.h); setSaturation(hsb.s); setBrightness(hsb.v);
        }
    }, [value]);

    // --- Spectrum interaction ---
    const handleSpectrumInteraction = useCallback((e) => {
        if (!spectrumRef.current) return;
        const rect = spectrumRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));
        const s = (x / rect.width) * 100;
        const b = 100 - (y / rect.height) * 100;
        setSaturation(s); setBrightness(b);
        const hex = hsbToHex(hue, s, b);
        setHexInput(hex); onChange(hex);
    }, [hue, onChange]);

    const handleHueInteraction = useCallback((e) => {
        if (!hueRef.current) return;
        const rect = hueRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const h = (x / rect.width) * 360;
        setHue(h);
        const hex = hsbToHex(h, saturation, brightness);
        setHexInput(hex); onChange(hex);
    }, [saturation, brightness, onChange]);

    useEffect(() => {
        if (!isDraggingSpectrum && !isDraggingHue) return;
        const onMove = (e) => { if (isDraggingSpectrum) handleSpectrumInteraction(e); if (isDraggingHue) handleHueInteraction(e); };
        const onUp = () => { setIsDraggingSpectrum(false); setIsDraggingHue(false); };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    }, [isDraggingSpectrum, isDraggingHue, handleSpectrumInteraction, handleHueInteraction]);

    // --- Handlers ---
    const selectColour = (hex) => {
        setHexInput(hex); onChange(hex);
        const hsb = hexToHsb(hex);
        setHue(hsb.h); setSaturation(hsb.s); setBrightness(hsb.v);
    };

    const handleHexChange = (e) => {
        let val = e.target.value;
        if (!val.startsWith('#')) val = '#' + val;
        setHexInput(val);
        if (/^#[0-9a-fA-F]{6}$/.test(val)) {
            const hsb = hexToHsb(val);
            setHue(hsb.h); setSaturation(hsb.s); setBrightness(hsb.v);
            onChange(val);
        }
    };

    const saveColour = () => {
        if (/^#[0-9a-fA-F]{6}$/.test(hexInput) && !savedColors.includes(hexInput.toLowerCase())) {
            const updated = [...savedColors, hexInput.toLowerCase()].slice(-24);
            setSavedColors(updated); persistSaved(updated);
        }
    };

    const textMode = getTextColour(value);
    const colourName = findColourName(value);

    // --- Render tabs ---
    const renderPalettes = () => (
        <div className="cp-palettes">
            {PALETTES.map((palette) => {
                const isActive = palette.colours.some(c => c.hex.toLowerCase() === value.toLowerCase());
                return (
                    <div key={palette.name} className={`cp-palette ${isActive ? 'cp-palette--active' : ''}`}>
                        <span className="cp-palette-name">{palette.name}</span>
                        <div className="cp-palette-strip">
                            {palette.colours.map((colour) => {
                                const selected = colour.hex.toLowerCase() === value.toLowerCase();
                                const tm = getTextColour(colour.hex);
                                return (
                                    <button key={colour.hex} type="button" title={colour.name}
                                        className={`cp-palette-swatch ${selected ? 'cp-palette-swatch--selected' : ''}`}
                                        style={{ background: colour.hex, color: tm === 'dark' ? '#29282D' : '#fff' }}
                                        onClick={() => selectColour(colour.hex)}>
                                        {selected ? (
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                                stroke={tm === 'dark' ? '#29282D' : '#fff'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12" />
                                            </svg>
                                        ) : (paletteAa ? 'Aa' : null)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const renderSwatches = () => (
        <div className="cp-swatches-tab">
            <div className="cp-grid">
                {SWATCHES.map((colour) => {
                    const selected = colour.hex.toLowerCase() === value.toLowerCase();
                    const tm = getTextColour(colour.hex);
                    return (
                        <button key={colour.hex} type="button" title={colour.name}
                            className={`cp-swatch ${selected ? 'cp-swatch--selected' : ''}`}
                            style={{ background: colour.hex }}
                            onClick={() => selectColour(colour.hex)}>
                            {selected && (
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                    stroke={tm === 'dark' ? '#29282D' : '#fff'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12" />
                                </svg>
                            )}
                        </button>
                    );
                })}
            </div>
            {savedColors.length > 0 && (
                <>
                    <p className="cp-saved-label">Saved</p>
                    <div className="cp-grid">
                        {savedColors.map((hex) => {
                            const selected = hex.toLowerCase() === value.toLowerCase();
                            const tm = getTextColour(hex);
                            return (
                                <button key={hex} type="button"
                                    className={`cp-swatch ${selected ? 'cp-swatch--selected' : ''}`}
                                    style={{ background: hex }}
                                    onClick={() => selectColour(hex)}>
                                    {selected && (
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                                            stroke={tm === 'dark' ? '#29282D' : '#fff'} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="20 6 9 17 4 12" />
                                        </svg>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );

    const renderPicker = () => (
        <div className="cp-picker-tab">
            {/* Spectrum square */}
            <div className="cp-spectrum" ref={spectrumRef}
                style={{ background: `hsl(${hue}, 100%, 50%)` }}
                onMouseDown={(e) => { setIsDraggingSpectrum(true); handleSpectrumInteraction(e); }}>
                <div className="cp-spectrum-white" />
                <div className="cp-spectrum-black" />
                <div className="cp-spectrum-cursor" style={{
                    left: `${saturation}%`, top: `${100 - brightness}%`,
                    borderColor: brightness > 50 ? '#29282D' : '#fff',
                }} />
            </div>
            {/* Hue slider */}
            <div className="cp-hue" ref={hueRef}
                onMouseDown={(e) => { setIsDraggingHue(true); handleHueInteraction(e); }}>
                <div className="cp-hue-cursor" style={{
                    left: `${(hue / 360) * 100}%`,
                    background: `hsl(${hue}, 100%, 50%)`,
                }} />
            </div>
        </div>
    );

    // --- Dialog (portaled) ---
    const dialog = isOpen ? ReactDOM.createPortal(
        <>
            <div className="cp-overlay" onClick={() => setIsOpen(false)} />
            <div className="cp-dialog">
                {/* Header */}
                <div className="cp-dialog-header">
                    <span className="cp-dialog-title">{title}</span>
                    <button type="button" className="cp-dialog-close" onClick={() => setIsOpen(false)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
                            <line x1="1" y1="1" x2="13" y2="13" /><line x1="13" y1="1" x2="1" y2="13" />
                        </svg>
                    </button>
                </div>

                {/* Tabs */}
                <div className="cp-tabs">
                    {TABS.filter(tab => palettes || tab.id !== 'palettes').map(tab => (
                        <button key={tab.id} type="button"
                            className={`cp-tab ${activeTab === tab.id ? 'cp-tab--active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}>
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab content */}
                <div className="cp-dialog-body">
                    {activeTab === 'palettes' && renderPalettes()}
                    {activeTab === 'swatches' && renderSwatches()}
                    {activeTab === 'picker' && renderPicker()}
                </div>

                {/* Bottom bar */}
                <div className="cp-bottom">
                    <span className="cp-bottom-preview" style={{ background: hexInput }}>
                        <span className={`cp-preview-text cp-preview-text--${getTextColour(hexInput)}`}>Aa</span>
                    </span>
                    <input type="text" className="cp-bottom-hex" value={hexInput}
                        onChange={handleHexChange} placeholder="#000000" maxLength={7} />
                    <button type="button" className={`cp-bottom-save ${savedColors.includes(hexInput.toLowerCase()) ? 'cp-bottom-save--saved' : ''}`}
                        onClick={saveColour} title="Save colour">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                            <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
                        </svg>
                    </button>
                </div>

                {/* Actions */}
                <div className="cp-dialog-footer">
                    {onReset && (
                        <button type="button" className="cp-reset-btn" onClick={() => { onReset(); setIsOpen(false); }}>
                            {resetLabel}
                        </button>
                    )}
                    <button type="button" className="cp-apply-btn" onClick={() => setIsOpen(false)}>
                        Apply
                    </button>
                </div>
            </div>
        </>,
        document.body
    ) : null;

    // Inline mode — render the picker UI directly (no trigger button,
    // no portal modal). For consumers like the voucher designer that
    // want the picker embedded in a sidebar instead of overlaying the
    // canvas. No Apply / Reset buttons either — changes are live.
    if (inline) {
        return (
            <div className="cp-inline">
                {/* Tabs wrapped so consumers can pin them via the
                    wrapper without disturbing the inner pill bg. */}
                <div className="cp-inline-tabs-bar">
                    <div className="cp-tabs">
                        {TABS.filter(tab => palettes || tab.id !== 'palettes').map(tab => (
                            <button key={tab.id} type="button"
                                className={`cp-tab ${activeTab === tab.id ? 'cp-tab--active' : ''}`}
                                onClick={() => setActiveTab(tab.id)}>
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="cp-inline-body">
                    {activeTab === 'palettes' && renderPalettes()}
                    {activeTab === 'swatches' && renderSwatches()}
                    {activeTab === 'picker' && renderPicker()}
                </div>
                <div className="cp-bottom">
                    <span className="cp-bottom-preview" style={{ background: hexInput }}>
                        <span className={`cp-preview-text cp-preview-text--${getTextColour(hexInput)}`}>Aa</span>
                    </span>
                    <input type="text" className="cp-bottom-hex" value={hexInput}
                        onChange={handleHexChange} placeholder="#000000" maxLength={7} />
                    <button type="button" className={`cp-bottom-save ${savedColors.includes(hexInput.toLowerCase()) ? 'cp-bottom-save--saved' : ''}`}
                        onClick={saveColour} title="Save colour">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                            <polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
                        </svg>
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="cp-wrapper">
            <button type="button" className="cp-trigger" onClick={() => setIsOpen(true)}>
                <span className="cp-preview" style={{ background: value }}>
                    <span className={`cp-preview-text cp-preview-text--${textMode}`}>Aa</span>
                </span>
                <span className="cp-trigger-label">{colourName || value}</span>
                <svg className="cp-trigger-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 4.5L6 7.5L9 4.5" />
                </svg>
            </button>
            {dialog}
        </div>
    );
};

export default ColourPicker;
